import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-92cc77c0"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  style: {"max-width":"800px","margin":"40px auto","padding":"1.5rem","border":"1px solid #e6e6e6","border-radius":"8px","background":"#fff"},
  class: "text-tertiary rounded"
}
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h1", { class: "text-3xl font-semibold mb-1" }, "Generate Word Meta", -1))
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", {
  style: {"margin-bottom":"0.5rem"},
  class: "text-md"
}, " Select a CSV file where each line contains a single word (no header). ", -1))
const _hoisted_4 = {
  key: 0,
  style: {"color":"#b91c1c","margin-top":"0.5rem"}
}
const _hoisted_5 = {
  key: 1,
  style: {"margin-top":"1.25rem"}
}
const _hoisted_6 = { style: {"max-height":"360px","overflow":"auto","border":"1px solid #f0f0f0","padding":"0.5rem","border-radius":"6px"} }
const _hoisted_7 = { style: {"width":"100%","border-collapse":"collapse","font-size":"0.95rem"} }
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("thead", null, [
  /*#__PURE__*/_createElementVNode("tr", null, [
    /*#__PURE__*/_createElementVNode("th", { style: {"text-align":"left","padding":"6px","border-bottom":"1px solid #eee"} }, "Word"),
    /*#__PURE__*/_createElementVNode("th", { style: {"text-align":"left","padding":"6px","border-bottom":"1px solid #eee"} }, "Status")
  ])
], -1))
const _hoisted_9 = { style: {"padding":"6px","vertical-align":"top"} }
const _hoisted_10 = { style: {"padding":"6px","vertical-align":"top"} }
const _hoisted_11 = { key: 0 }
const _hoisted_12 = { style: {"margin-top":"1rem","display":"flex","align-items":"center","gap":"1rem"} }
const _hoisted_13 = ["disabled"]
const _hoisted_14 = {
  key: 1,
  style: {"margin-left":"auto"}
}
const _hoisted_15 = {
  key: 0,
  style: {"color":"#b91c1c","margin-top":"0.5rem"}
}
const _hoisted_16 = {
  key: 1,
  style: {"color":"#15803d","margin-top":"0.5rem"}
}

import { ref, onMounted } from "vue";
import api from "@/api";
import PageHeaderComponent from "@/components/global/PageHeaderComponent.vue";

type CSVRow = { word: string };


export default /*#__PURE__*/_defineComponent({
  __name: 'LingoWordUploadMetaView',
  setup(__props) {

const records = ref<CSVRow[]>([]);
const parseError = ref<string>("");
const uploading = ref(false);
const uploadStatuses = ref<string[]>([]);
const uploadedCount = ref(0);
const controller = ref<AbortController | null>(null);
const overallError = ref<string>("");
const uploadComplete = ref(false);

function onFileChange(e: Event) {
  parseError.value = "";
  const input = e.target as HTMLInputElement;
  if (!input.files || !input.files.length) return;
  const file = input.files[0];
  const reader = new FileReader();

  reader.onload = () => {
    const text = reader.result as string;
    try {
      const rows = parseSingleColumnCSV(text);
      records.value = rows;
      uploadStatuses.value = new Array(rows.length).fill("");
      uploadedCount.value = 0;
      uploadComplete.value = false;
      overallError.value = "";
    } catch (err: any) {
      parseError.value = err?.message || "Failed to parse file";
      records.value = [];
      uploadStatuses.value = [];
    }
  };

  reader.onerror = () => {
    parseError.value = "Failed to read file.";
  };

  reader.readAsText(file);
  // clear the input element to allow re-uploading same file later
  (e.target as HTMLInputElement).value = "";
}

function parseSingleColumnCSV(text: string): CSVRow[] {
  if (!text || !text.toString().trim()) throw new Error("File is empty.");

  // Normalize newlines
  const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const rawLines = normalized.split("\n");

  const rows: CSVRow[] = [];
  for (let i = 0; i < rawLines.length; i++) {
    const raw = rawLines[i].trim();
    if (!raw) continue; // skip empty lines

    // If line contains commas, parse first CSV field (handles simple quoted values)
    let word = raw;
    if (raw.includes(",")) {
      // basic CSV first-field extraction with quote support
      let inQuotes = false;
      let cur = "";
      for (let c = 0; c < raw.length; c++) {
        const ch = raw[c];
        if (ch === '"') {
          if (inQuotes && raw[c + 1] === '"') {
            cur += '"';
            c++;
            continue;
          }
          inQuotes = !inQuotes;
          continue;
        }
        if (ch === "," && !inQuotes) break;
        cur += ch;
      }
      word = cur.trim();
    }

    if (!word) continue;
    rows.push({ word });
  }

  if (rows.length === 0) throw new Error("No words found in file.");

  return rows;
}

function previewCell(value: any) {
  return String(value ?? "");
}

function preparePayload(row: CSVRow) {
  return { word: (row.word || "").trim() };
}

async function startUpload() {
  if (!records.value.length) return;
  uploading.value = true;
  uploadedCount.value = 0;
  uploadStatuses.value = new Array(records.value.length).fill("Pending");
  overallError.value = "";
  uploadComplete.value = false;
  controller.value = new AbortController();

  const endpoint = "v2/lingo-v2/word-upload-meta/upsert-word";

  for (let i = 0; i < records.value.length; i++) {
    if (!uploading.value) break; // cancelled
    try {
      uploadStatuses.value[i] = "Uploading...";
      const payload = preparePayload(records.value[i]);

      // axios usage: send payload as body, pass signal & timeout in config
      const resp = await api.put(endpoint, payload, {
        signal: controller.value?.signal,
        timeout: 45000,
      });

      const status = resp?.status ?? resp?.statusCode ?? null;

      if (status === 200 || status === 201) {
        uploadStatuses.value[i] = "Completed";
        uploadedCount.value++;
      } else if (status) {
        uploadStatuses.value[i] = `Error: unexpected status ${status}`;
        overallError.value = overallError.value || `One or more rows failed (status ${status}).`;
      } else {
        uploadStatuses.value[i] = "Error: no response (timeout/network)";
        overallError.value =
          overallError.value || "Upload failed: no response from server (timeout or network error).";
      }
    } catch (err: any) {
      if (err && err.name === "CanceledError") {
        // axios throws Cancel or CanceledError depending on version
        uploadStatuses.value[i] = "Canceled";
        overallError.value = "Upload canceled by user.";
        break;
      }
      const serverMessage =
        err?.response?.data?.message ||
        err?.message ||
        (err?.status ? `status ${err.status}` : null) ||
        (err?.statusCode ? `status ${err.statusCode}` : null);

      uploadStatuses.value[i] = `Error: ${serverMessage || "Failed (network/timeout)"}`;
      overallError.value = overallError.value || serverMessage || "Upload failed for one or more rows.";
      // continue with next row
    }
  }

  uploading.value = false;
  controller.value = null;
  uploadComplete.value = uploadedCount.value === records.value.length && !overallError.value;
}

function cancelUpload() {
  if (controller.value) {
    controller.value.abort();
    uploading.value = false;
    controller.value = null;
  }
  // set all remaining pending to canceled
  for (let i = 0; i < uploadStatuses.value.length; i++) {
    if (uploadStatuses.value[i] === "Pending" || uploadStatuses.value[i] === "Uploading...") {
      uploadStatuses.value[i] = "Canceled";
      overallError.value = "Upload canceled by user.";
    }
  }
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(PageHeaderComponent, { pageHeading: 'Lingo V2' }),
    _createElementVNode("div", null, [
      _createElementVNode("div", _hoisted_1, [
        _hoisted_2,
        _hoisted_3,
        _createElementVNode("input", {
          type: "file",
          accept: ".csv,.txt,text/csv,text/plain",
          onChange: onFileChange
        }, null, 32),
        (parseError.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(parseError.value), 1))
          : _createCommentVNode("", true),
        (records.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("h3", null, "Preview (" + _toDisplayString(records.value.length) + " rows)", 1),
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("table", _hoisted_7, [
                  _hoisted_8,
                  _createElementVNode("tbody", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(records.value, (r, idx) => {
                      return (_openBlock(), _createElementBlock("tr", {
                        key: idx,
                        style: _normalizeStyle({ background: idx % 2 ? '#fafafa' : '#fff' })
                      }, [
                        _createElementVNode("td", _hoisted_9, _toDisplayString(previewCell(r.word)), 1),
                        _createElementVNode("td", _hoisted_10, [
                          (uploadStatuses.value[idx])
                            ? (_openBlock(), _createElementBlock("small", _hoisted_11, _toDisplayString(uploadStatuses.value[idx]), 1))
                            : _createCommentVNode("", true)
                        ])
                      ], 4))
                    }), 128))
                  ])
                ])
              ]),
              _createElementVNode("div", _hoisted_12, [
                _createElementVNode("button", {
                  class: "rounded-md bg-blue-600 px-2.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600",
                  disabled: uploading.value,
                  onClick: startUpload
                }, " Upload " + _toDisplayString(records.value.length) + " rows ", 9, _hoisted_13),
                (uploading.value)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      onClick: cancelUpload,
                      class: "rounded-md bg-red-600 px-2.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                    }, " Cancel "))
                  : _createCommentVNode("", true),
                (uploading.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_14, "Progress: " + _toDisplayString(uploadedCount.value) + "/" + _toDisplayString(records.value.length), 1))
                  : _createCommentVNode("", true)
              ]),
              (overallError.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_15, _toDisplayString(overallError.value), 1))
                : _createCommentVNode("", true),
              (uploadComplete.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_16, "Upload complete."))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ])
    ])
  ], 64))
}
}

})